import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { PageContainer } from "@swot/base-components";
import { ApiErrorPanel } from "@components/errors/index.js";
import { CmtsPageHintPanel } from "@components/hints/index.js";
import { useApiError } from "@hooks/useApiError.js";
import { dashboardApi } from "@services/dashboardApi.js";
import { CmtsKpiCardGrid, type CmtsDashboardKpi } from "./CmtsKpiCardGrid.js";
import { CmtsDashboardScopePanel } from "./CmtsDashboardScopePanel.js";
import { readCmtsSession } from "@cmts/auth/authStore.js";

const LazyChartPanel = lazy(() => import("./CmtsDashboardChartPanel.js"));

type DashboardDto = Record<string, unknown>;

export type CmtsDashboardShellProps = {
  readonly dashboardType?: string | undefined;
  readonly readOnly?: boolean | undefined;
  readonly dashboard?: Record<string, unknown> | null | undefined;
  readonly error?: unknown | undefined;
};

export function CmtsDashboardShell(props: CmtsDashboardShellProps) {
  const dashboardType = props.dashboardType ?? "management";
  const [dashboard, setDashboard] = useState<DashboardDto | null>(props.dashboard ?? null);
  const [loading, setLoading] = useState(false);
  const apiError = useApiError();
  useEffect(() => {
    // A parent container (e.g. CmtsDashboardsContainer) already attempted the fetch and is
    // telling us it failed -- surface that instead of quietly rendering an empty dashboard.
    if (props.error) {
      apiError.setError(props.error, { operation: "dashboard.load", entityType: "Dashboard", entityId: dashboardType });
      setDashboard(null);
      setLoading(false);
      return;
    }
    if (props.dashboard) {
      apiError.clearError();
      setDashboard(props.dashboard);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    apiError.clearError();
    dashboardApi.getDashboardByType(dashboardType, queryFromLocation(), controller.signal)
      .then((data) => setDashboard(data))
      .catch((error: unknown) => { if (!controller.signal.aborted) apiError.setError(error, { operation: "dashboard.load", entityType: "Dashboard", entityId: dashboardType }); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardType, props.dashboard, props.error]);

  const cards = useMemo(() => readCards(dashboard), [dashboard]);
  const title = String(dashboard?.title ?? titleForDashboard(dashboardType));
  const subtitle = props.readOnly ? "Read-only backend-scoped executive dashboard." : "Backend-scoped dashboard metrics and drilldowns.";
  const scope = readRecord(dashboard?.scope ?? dashboard?.permissionScope ?? dashboard?.filters);
  const chartQuery = useMemo(() => {
  const session = readCmtsSession();

  const tenantId = String(
    dashboard?.tenantId ??
    scope.tenantId ??
    session?.tenantId ??
    "",
  );

  const query = scopeForDrilldown(scope);

  return tenantId
    ? {
        ...query,
        tenantId,
      }
    : query;
}, [dashboard, scope]);

  return (
    <PageContainer title={title} subtitle={subtitle}>
      {apiError.error ? <ApiErrorPanel error={apiError.error} onRetry={() => window.location.reload()} /> : null}
      <CmtsPageHintPanel defaultOpen={false}>
        Dashboard KPIs, operational workload metrics, and trend charts are evaluated strictly within tenant, permission, and organization boundaries.
      </CmtsPageHintPanel>

      <section className="cmts-workspace-hero">
        <div>
          <p className="cmts-section-eyebrow">LIVE · ROLE-SCOPED OPERATIONS</p>
          <h2>{title}</h2>
          <p>Backend-authoritative KPIs, workload signals and drilldowns for the signed-in user.</p>
        </div>
        <div className="cmts-page-actions">
          <button
            type="button"
            className="cmts-secondary-button"
            onClick={() => window.location.reload()}
          >
            {loading ? "Refreshing…" : "Refresh dashboard"}
          </button>
        </div>
      </section>
      <CmtsDashboardScopePanel scope={scope} readOnly={props.readOnly ?? dashboardType === "executive"} />
      {loading ? <div className="cmts-loading-panel">Loading dashboard from backend…</div> : <CmtsKpiCardGrid cards={cards} scope={scopeForDrilldown(scope)} readOnly={props.readOnly} />}
      <Suspense fallback={<div className="cmts-loading-panel">Loading chart module…</div>}>
<LazyChartPanel
  chartKey={`${dashboardType}-trend`}
  title="Backend chart data"
  query={chartQuery}
/>      </Suspense>
    </PageContainer>
  );
}

function queryFromLocation() {
  const params = new URLSearchParams(typeof window === "undefined" ? "" : window.location.search);
  const filters: Record<string, string> = {};
  for (const [key, value] of params.entries()) filters[key] = value;
  return { filters };
}
function readCards(input: DashboardDto | null): readonly CmtsDashboardKpi[] {
  const raw = input?.cards ?? input?.kpis ?? input?.items;
  return Array.isArray(raw) ? raw.filter(isRecord) as CmtsDashboardKpi[] : [];
}
function readRecord(value: unknown): Record<string, unknown> { return isRecord(value) ? value : {}; }
function scopeForDrilldown(value: Record<string, unknown>): Record<string, string | number | boolean> {
  const output: Record<string, string | number | boolean> = {};
  for (const [key, item] of Object.entries(value)) if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") output[key] = item;
  return output;
}
function isRecord(value: unknown): value is Record<string, unknown> { return typeof value === "object" && value !== null && !Array.isArray(value); }
function titleForDashboard(type: string): string { return type === "executive" ? "Executive Dashboard Viewer" : "CMTS Dashboard"; }
